import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Video,
  Mic,
  User,
  Brain,
  Clock,
  Target,
  Star,
  Play,
  Pause,
  Square,
  RotateCcw,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Award,
  BookOpen,
  MessageSquare,
  Volume2,
  Eye,
  Smile,
  Zap,
  BarChart3,
  Lightbulb,
  ArrowRight,
  Settings,
  Download
} from 'lucide-react';
import { Card } from '../shared/Card';
import { Button } from '../shared/Button';
import { Badge } from '../shared/Badge';
import { ProgressBar } from '../shared/ProgressBar';

interface InterviewQuestion {
  id: string;
  category: 'general' | 'academic' | 'personal' | 'behavioral' | 'specific';
  difficulty: 'easy' | 'medium' | 'hard';
  question: string;
  context: string;
  tips: string[];
  sampleAnswer: string;
  timeLimit: number;
}

interface InterviewSession {
  id: string;
  date: string;
  duration: number;
  questionsAsked: number;
  questionsAnswered: number;
  averageScore: number;
  categories: string[];
  feedback: {
    content: number;
    delivery: number;
    confidence: number;
    clarity: number;
    overall: number;
  };
}

interface AIFeedback {
  category: 'content' | 'delivery' | 'confidence' | 'clarity';
  score: number;
  feedback: string;
  improvements: string[];
  strengths: string[];
}

const interviewQuestions: InterviewQuestion[] = [
  {
    id: '1',
    category: 'general',
    difficulty: 'easy',
    question: 'Tell me about yourself.',
    context: 'This is typically the opening question in most college interviews. It\'s your chance to make a strong first impression.',
    tips: [
      'Keep it concise (2-3 minutes)',
      'Focus on academic and personal highlights',
      'Connect your past experiences to future goals',
      'End with why you\'re interested in this school'
    ],
    sampleAnswer: 'I\'m a high school senior passionate about environmental science and community service. I\'ve spent the last two years leading our school\'s environmental club, where we implemented a campus-wide recycling program that reduced waste by 40%. This experience taught me the importance of data-driven solutions to environmental challenges. I\'m particularly drawn to your university because of its renowned environmental science program and opportunities for undergraduate research. I\'m excited about the possibility of contributing to ongoing sustainability initiatives on campus.',
    timeLimit: 180
  },
  {
    id: '2',
    category: 'academic',
    difficulty: 'medium',
    question: 'What academic subject challenges you the most, and how do you overcome those challenges?',
    context: 'This question assesses your self-awareness, problem-solving skills, and resilience in academics.',
    tips: [
      'Be honest about a real challenge',
      'Focus on your problem-solving approach',
      'Show growth mindset and persistence',
      'Demonstrate specific strategies you use'
    ],
    sampleAnswer: 'Chemistry has been my most challenging subject, particularly balancing chemical equations and understanding reaction mechanisms. Initially, I struggled with visualizing molecular interactions. To overcome this, I started using molecular modeling software and formed a study group with classmates. I also began connecting chemistry concepts to real-world applications, like understanding how photosynthesis works in my environmental science research. This approach helped me improve my grade from a B- to an A- this semester, and more importantly, I now find chemistry fascinating rather than intimidating.',
    timeLimit: 120
  },
  {
    id: '3',
    category: 'personal',
    difficulty: 'medium',
    question: 'Describe a time when you had to work with someone whose background or perspective was very different from yours.',
    context: 'This question evaluates your cultural competency, collaboration skills, and ability to work in diverse environments.',
    tips: [
      'Choose a specific, meaningful example',
      'Highlight what you learned from the experience',
      'Show respect for different perspectives',
      'Demonstrate growth in cultural awareness'
    ],
    sampleAnswer: 'Last summer, I volunteered at a community center in a neighborhood very different from mine. I was paired with Maria, a recent immigrant from El Salvador, to organize a youth soccer program. Initially, we had communication challenges and different ideas about program structure. However, I learned that Maria had extensive experience coaching youth sports in her home country. By listening carefully and learning some Spanish, we combined her coaching expertise with my organizational skills. The program became the most successful in the center\'s history, teaching me that diversity of experience and perspective creates stronger solutions.',
    timeLimit: 150
  },
  {
    id: '4',
    category: 'behavioral',
    difficulty: 'hard',
    question: 'Tell me about a time when you failed at something important to you. How did you handle it?',
    context: 'This question tests your resilience, self-reflection, and ability to learn from setbacks.',
    tips: [
      'Choose a genuine failure with meaningful stakes',
      'Take responsibility without making excuses',
      'Focus on what you learned and how you grew',
      'Show how the failure led to future success'
    ],
    sampleAnswer: 'In my junior year, I ran for student body president with ambitious plans for campus improvements. Despite campaigning hard, I lost by a significant margin. Initially, I was devastated and considered stepping back from student leadership entirely. However, I asked the winner and several classmates for honest feedback. I learned that my ideas were good but I hadn\'t listened enough to what students actually wanted. I used this feedback to run for a class representative position, where I focused on grassroots organizing and student surveys. I won that election and successfully implemented three major student initiatives. This experience taught me that failure is often about approach, not ability, and that listening is as important as leading.',
    timeLimit: 180
  },
  {
    id: '5',
    category: 'specific',
    difficulty: 'hard',
    question: 'Why do you want to attend our university specifically?',
    context: 'This question tests your genuine interest in the school and how well you\'ve researched their programs.',
    tips: [
      'Research specific programs, professors, or opportunities',
      'Connect university features to your goals',
      'Mention unique aspects of the school',
      'Show genuine enthusiasm'
    ],
    sampleAnswer: 'Your university stands out for three key reasons that align perfectly with my goals. First, Professor Johnson\'s research on sustainable agriculture directly relates to my interest in combining environmental science with food security. I\'m excited about the possibility of joining her lab as an undergraduate researcher. Second, the Environmental Studies program\'s interdisciplinary approach, combining science, policy, and economics, matches my belief that environmental challenges require holistic solutions. Finally, the university\'s commitment to carbon neutrality by 2030 shows that the institution practices what it teaches. I want to be part of a community that\'s actively working toward the future I want to help create.',
    timeLimit: 120
  }
];

const recentSessions: InterviewSession[] = [
  {
    id: '1',
    date: '2024-09-20',
    duration: 25,
    questionsAsked: 8,
    questionsAnswered: 8,
    averageScore: 78,
    categories: ['general', 'academic', 'personal'],
    feedback: {
      content: 82,
      delivery: 75,
      confidence: 80,
      clarity: 73,
      overall: 78
    }
  },
  {
    id: '2',
    date: '2024-09-18',
    duration: 30,
    questionsAsked: 10,
    questionsAnswered: 9,
    averageScore: 71,
    categories: ['behavioral', 'specific', 'academic'],
    feedback: {
      content: 76,
      delivery: 68,
      confidence: 70,
      clarity: 69,
      overall: 71
    }
  }
];

export const InterviewPrep: React.FC = () => {
  const [selectedMode, setSelectedMode] = useState<'practice' | 'analysis' | 'history'>('practice');
  const [currentQuestion, setCurrentQuestion] = useState<InterviewQuestion | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [questionsCompleted, setQuestionsCompleted] = useState(0);
  const [currentFeedback, setCurrentFeedback] = useState<AIFeedback[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  const startSession = () => {
    setSessionStarted(true);
    setQuestionsCompleted(0);
    getNextQuestion();
  };

  const getNextQuestion = () => {
    let filteredQuestions = interviewQuestions;

    if (selectedCategory !== 'all') {
      filteredQuestions = filteredQuestions.filter(q => q.category === selectedCategory);
    }

    if (selectedDifficulty !== 'all') {
      filteredQuestions = filteredQuestions.filter(q => q.difficulty === selectedDifficulty);
    }

    const randomQuestion = filteredQuestions[Math.floor(Math.random() * filteredQuestions.length)];
    setCurrentQuestion(randomQuestion);
    setRecordingTime(0);
    setCurrentFeedback([]);
  };

  const startRecording = () => {
    setIsRecording(true);
    setRecordingTime(0);
  };

  const stopRecording = () => {
    setIsRecording(false);
    generateAIFeedback();
  };

  const generateAIFeedback = () => {
    // Simulate AI analysis
    setTimeout(() => {
      const feedback: AIFeedback[] = [
        {
          category: 'content',
          score: Math.floor(Math.random() * 20) + 75,
          feedback: 'Your answer demonstrated good knowledge of the topic and included relevant examples.',
          improvements: ['Add more specific details to strengthen your examples', 'Connect your answer more clearly to your goals'],
          strengths: ['Clear structure and logical flow', 'Good use of personal examples']
        },
        {
          category: 'delivery',
          score: Math.floor(Math.random() * 20) + 70,
          feedback: 'Your speaking pace was appropriate, though you could work on vocal variety.',
          improvements: ['Vary your tone to emphasize key points', 'Use strategic pauses for impact'],
          strengths: ['Clear pronunciation', 'Appropriate volume and pace']
        },
        {
          category: 'confidence',
          score: Math.floor(Math.random() * 20) + 72,
          feedback: 'You showed good confidence throughout most of your response.',
          improvements: ['Maintain eye contact throughout your response', 'Reduce filler words (um, uh)'],
          strengths: ['Strong opening', 'Maintained composure under pressure']
        },
        {
          category: 'clarity',
          score: Math.floor(Math.random() * 20) + 68,
          feedback: 'Your ideas were generally clear but could be more concise.',
          improvements: ['Practice summarizing key points more succinctly', 'Use more transition phrases'],
          strengths: ['Logical organization', 'Clear conclusion']
        }
      ];
      setCurrentFeedback(feedback);
      setQuestionsCompleted(prev => prev + 1);
    }, 2000);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'general': return User;
      case 'academic': return BookOpen;
      case 'personal': return Smile;
      case 'behavioral': return Brain;
      case 'specific': return Target;
      default: return MessageSquare;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <Card className="bg-gradient-to-br from-primary-500 to-accent-500 text-white">
        <div className="text-center">
          <Video className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">AI Interview Preparation</h2>
          <p className="text-primary-100 text-lg mb-6">
            Practice college interviews with AI-powered feedback and personalized coaching
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{interviewQuestions.length}</div>
              <div className="text-sm text-primary-100">Practice Questions</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">AI</div>
              <div className="text-sm text-primary-100">Powered Feedback</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{recentSessions.length}</div>
              <div className="text-sm text-primary-100">Practice Sessions</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Navigation */}
      <Card>
        <div className="flex border-b border-gray-200">
          {[
            { id: 'practice', label: 'Practice Interview', icon: Video },
            { id: 'analysis', label: 'Performance Analysis', icon: BarChart3 },
            { id: 'history', label: 'Session History', icon: Clock }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setSelectedMode(id as any)}
              className={`flex items-center space-x-2 px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                selectedMode === id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={16} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* Content Based on Selected Mode */}
      <AnimatePresence mode="wait">
        {selectedMode === 'practice' && (
          <motion.div
            key="practice"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            {!sessionStarted ? (
              /* Session Setup */
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">Interview Setup</h3>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">Question Category</label>
                      <div className="grid grid-cols-2 gap-2">
                        {['all', 'general', 'academic', 'personal', 'behavioral', 'specific'].map((category) => (
                          <button
                            key={category}
                            onClick={() => setSelectedCategory(category)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              selectedCategory === category
                                ? 'bg-primary-100 text-primary-700 border border-primary-300'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">Difficulty Level</label>
                      <div className="grid grid-cols-2 gap-2">
                        {['all', 'easy', 'medium', 'hard'].map((difficulty) => (
                          <button
                            key={difficulty}
                            onClick={() => setSelectedDifficulty(difficulty)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              selectedDifficulty === difficulty
                                ? 'bg-primary-100 text-primary-700 border border-primary-300'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <Button onClick={startSession} className="w-full" size="lg">
                      <Play size={20} className="mr-2" />
                      Start Interview Practice
                    </Button>
                  </div>
                </Card>

                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">How It Works</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-primary-600">1</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">Question Presentation</h4>
                        <p className="text-sm text-gray-600">AI presents interview questions based on your selected criteria</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-primary-600">2</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">Record Response</h4>
                        <p className="text-sm text-gray-600">Practice answering while being recorded for analysis</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-primary-600">3</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">AI Analysis</h4>
                        <p className="text-sm text-gray-600">Receive detailed feedback on content, delivery, and confidence</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-primary-600">4</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">Improve & Repeat</h4>
                        <p className="text-sm text-gray-600">Apply feedback and continue practicing with new questions</p>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            ) : (
              /* Active Session */
              <div className="space-y-6">
                {/* Session Status */}
                <Card>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Interview Session</h3>
                    <div className="flex items-center space-x-4">
                      <Badge variant="primary">
                        Question {questionsCompleted + 1}
                      </Badge>
                      <Button variant="outline" size="sm" onClick={() => setSessionStarted(false)}>
                        End Session
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-2xl font-bold text-primary-600">{questionsCompleted}</div>
                      <div className="text-sm text-gray-600">Questions Completed</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-2xl font-bold text-green-600">{formatTime(recordingTime)}</div>
                      <div className="text-sm text-gray-600">Current Response Time</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-2xl font-bold text-blue-600">
                        {currentFeedback.length > 0
                          ? Math.round(currentFeedback.reduce((acc, f) => acc + f.score, 0) / currentFeedback.length)
                          : '--'
                        }
                      </div>
                      <div className="text-sm text-gray-600">Average Score</div>
                    </div>
                  </div>
                </Card>

                {/* Current Question */}
                {currentQuestion && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <div className="flex items-center space-x-3 mb-4">
                        {React.createElement(getCategoryIcon(currentQuestion.category), {
                          size: 20,
                          className: 'text-primary-500'
                        })}
                        <div>
                          <Badge variant="secondary" size="sm">{currentQuestion.category}</Badge>
                          <Badge variant={currentQuestion.difficulty === 'hard' ? 'warning' : 'primary'} size="sm" className="ml-2">
                            {currentQuestion.difficulty}
                          </Badge>
                        </div>
                      </div>

                      <h4 className="text-lg font-semibold text-gray-900 mb-4">
                        {currentQuestion.question}
                      </h4>

                      <div className="bg-blue-50 rounded-lg p-4 mb-4">
                        <h5 className="font-semibold text-blue-800 mb-2">Context:</h5>
                        <p className="text-sm text-blue-700">{currentQuestion.context}</p>
                      </div>

                      <div className="space-y-3">
                        <h5 className="font-semibold text-gray-800">Tips for this question:</h5>
                        <ul className="space-y-1">
                          {currentQuestion.tips.map((tip, index) => (
                            <li key={index} className="text-sm text-gray-600 flex items-start space-x-2">
                              <Lightbulb size={14} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="flex items-center space-x-2 mt-6 text-sm text-gray-500">
                        <Clock size={14} />
                        <span>Recommended time: {Math.floor(currentQuestion.timeLimit / 60)}:{(currentQuestion.timeLimit % 60).toString().padStart(2, '0')}</span>
                      </div>
                    </Card>

                    <Card>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Your Response</h4>

                      {/* Recording Controls */}
                      <div className="text-center mb-6">
                        <div className="w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          {isRecording ? (
                            <div className="w-20 h-20 bg-red-500 rounded-full animate-pulse flex items-center justify-center">
                              <Mic className="text-white" size={32} />
                            </div>
                          ) : (
                            <div className="w-20 h-20 bg-gray-300 rounded-full flex items-center justify-center">
                              <Mic className="text-gray-500" size={32} />
                            </div>
                          )}
                        </div>

                        <div className="text-2xl font-bold text-gray-900 mb-4">
                          {formatTime(recordingTime)}
                        </div>

                        <div className="flex justify-center space-x-4">
                          {!isRecording ? (
                            <Button onClick={startRecording} size="lg">
                              <Play size={20} className="mr-2" />
                              Start Recording
                            </Button>
                          ) : (
                            <Button onClick={stopRecording} variant="outline" size="lg">
                              <Square size={20} className="mr-2" />
                              Stop Recording
                            </Button>
                          )}
                          <Button variant="ghost" size="lg" onClick={() => setRecordingTime(0)}>
                            <RotateCcw size={20} />
                          </Button>
                        </div>
                      </div>

                      {/* AI Feedback */}
                      {currentFeedback.length > 0 && (
                        <div className="space-y-4">
                          <h5 className="font-semibold text-gray-800">AI Feedback:</h5>
                          {currentFeedback.map((feedback, index) => (
                            <div key={feedback.category} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <h6 className="font-semibold text-gray-900 capitalize">{feedback.category}</h6>
                                <span className={`text-lg font-bold ${getScoreColor(feedback.score)}`}>
                                  {feedback.score}/100
                                </span>
                              </div>
                              <p className="text-sm text-gray-700 mb-3">{feedback.feedback}</p>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                  <h6 className="font-medium text-green-700">Strengths:</h6>
                                  <ul className="mt-1 space-y-1">
                                    {feedback.strengths.map((strength, idx) => (
                                      <li key={idx} className="text-green-600 flex items-center space-x-1">
                                        <CheckCircle size={12} />
                                        <span>{strength}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                <div>
                                  <h6 className="font-medium text-orange-700">Improvements:</h6>
                                  <ul className="mt-1 space-y-1">
                                    {feedback.improvements.map((improvement, idx) => (
                                      <li key={idx} className="text-orange-600 flex items-center space-x-1">
                                        <ArrowRight size={12} />
                                        <span>{improvement}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          ))}

                          <div className="flex justify-center space-x-4 mt-6">
                            <Button onClick={getNextQuestion}>
                              Next Question
                            </Button>
                            <Button variant="outline" onClick={() => setCurrentQuestion(null)}>
                              View Sample Answer
                            </Button>
                          </div>
                        </div>
                      )}
                    </Card>
                  </div>
                )}

                {/* Sample Answer Modal */}
                {!currentQuestion && (
                  <Card>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Sample Answer</h4>
                    <div className="bg-green-50 rounded-lg p-4 mb-4">
                      <p className="text-gray-700">{interviewQuestions[0].sampleAnswer}</p>
                    </div>
                    <div className="flex justify-center space-x-4">
                      <Button onClick={getNextQuestion}>
                        Next Question
                      </Button>
                      <Button variant="outline" onClick={() => setCurrentQuestion(interviewQuestions[0])}>
                        Back to Question
                      </Button>
                    </div>
                  </Card>
                )}
              </div>
            )}
          </motion.div>
        )}

        {selectedMode === 'history' && (
          <motion.div
            key="history"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Practice Session History</h3>
              <div className="space-y-4">
                {recentSessions.map((session, index) => (
                  <div key={session.id} className="border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="font-semibold text-gray-900">Session {recentSessions.length - index}</h4>
                        <p className="text-sm text-gray-600">{new Date(session.date).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <div className={`text-2xl font-bold ${getScoreColor(session.averageScore)}`}>
                          {session.averageScore}%
                        </div>
                        <div className="text-sm text-gray-600">Overall Score</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="text-center">
                        <div className="text-lg font-bold text-blue-600">{session.duration}m</div>
                        <div className="text-xs text-gray-600">Duration</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-600">{session.questionsAnswered}/{session.questionsAsked}</div>
                        <div className="text-xs text-gray-600">Questions</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-purple-600">{session.categories.length}</div>
                        <div className="text-xs text-gray-600">Categories</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-orange-600">{session.feedback.content}</div>
                        <div className="text-xs text-gray-600">Top Score</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(session.feedback).filter(([key]) => key !== 'overall').map(([category, score]) => (
                        <div key={category}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium text-gray-700 capitalize">{category}</span>
                            <span className={`text-sm font-semibold ${getScoreColor(score)}`}>{score}%</span>
                          </div>
                          <ProgressBar progress={score} color="primary" size="sm" />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};